import * as React from 'react';
import { useSelector } from 'react-redux';
import { EMediaType, TMedia } from 'shared/components/NewbuildingGallery';
import { FloorPlan } from 'shared/components/NewbuildingGallery/components/FloorPlan';
import THUMB_SRC from 'shared/components/offer_gallery/createMortgageFrame/img/thumb.svg';
import { use3D } from 'shared/containers/NewbuildingGalleryContainer/utils/use3D';
import { useMiddleBanner } from 'shared/containers/NewbuildingGalleryContainer/utils/useMiddleBanner';
import { useMortgageBanner } from 'shared/containers/NewbuildingGalleryContainer/utils/useMortgageBanner';
import { selectOffer } from 'shared/selectors';
import { getPhotos } from 'shared/selectors/newObject';
import { getFloorPlan } from 'shared/selectors/newObject/getFloorPlan';
import { getLayout } from 'shared/selectors/newObject/getLayout';
import { selectIsBookedFromDeveloper } from 'shared/selectors/newbuildingBookedFromDeveloper';

import { MAP_VIDEO_TYPE_TO_MEDIA_TYPE, MIDDLE_BANNER_SLIDE_LIMIT, MIDDLE_BANNER_SLIDE_POSITION } from '../constants';

export const useNewbuildingMedia = () => {
  const { videos } = useSelector(selectOffer);
  const floorPlan = useSelector(getFloorPlan);
  const layout = useSelector(getLayout);
  const photos = useSelector(getPhotos);
  const isBookedFromDeveloper = useSelector(selectIsBookedFromDeveloper);
  const mortgageBanner = useMortgageBanner(THUMB_SRC);
  const middleBanner = useMiddleBanner(THUMB_SRC);
  const tour = use3D();

  const media: TMedia[] = React.useMemo(() => {
    const media: TMedia[] = [];

    if (layout && layout.fullUrl && layout.thumbnailUrl) {
      media.push({
        type: EMediaType.Layout,
        src: layout.fullUrl,
        thumbnailSrc: layout.thumbnailUrl,
      });
    }

    if (floorPlan && floorPlan.fullUrl && floorPlan.thumbnailUrl) {
      media.push({
        type: EMediaType.FloorPlan,
        component: <FloorPlan fullUrl={floorPlan.fullUrl} polygonPath={floorPlan.polygonPath} />,
        thumbnailSrc: floorPlan.thumbnailUrl,
      });
    }

    if (tour) {
      media.push(tour);
    }

    if (videos) {
      media.push(
        ...videos.map<TMedia>(
          ({ type, url: src, previewUrl = '', id }) =>
            ({
              type: MAP_VIDEO_TYPE_TO_MEDIA_TYPE[type],
              previewUrl,
              src,
              id,
            }) as TMedia,
        ),
      );
    }

    /* istanbul ignore else */
    if (photos) {
      const photoMedias = photos.map<TMedia>(p => ({
        type: EMediaType.Photo,
        src: p.fullUrl as string,
        thumbnailSrc: p.thumbnailUrl as string,
      }));

      media.push(...photoMedias);
    }

    if (middleBanner && !isBookedFromDeveloper) {
      if (media.length > MIDDLE_BANNER_SLIDE_LIMIT) {
        media.splice(MIDDLE_BANNER_SLIDE_POSITION, 0, middleBanner);
      } else {
        media.push(middleBanner);
      }
    }

    if (mortgageBanner && !isBookedFromDeveloper) {
      media.push(mortgageBanner);
    }

    return media;
  }, [floorPlan, isBookedFromDeveloper, layout, middleBanner, mortgageBanner, photos, tour, videos]);

  return media;
};
